import React from 'react';
import i18n from '@/i18n';

type Variant = 'full' | 'inline';

interface AuthLoadingFallbackProps {
  variant?: Variant;
  labelKey?: string;
}

/**
 * Unified loading fallback for auth-related guards.
 *
 * Two sizes only — they express real, different UX contexts:
 *   - `full` (default): viewport-height spinner. Use when no app UI
 *     has rendered yet and the guard owns the whole screen (e.g.
 *     `RouteGuard` while the auth context hydrates on first paint).
 *   - `inline`: 40vh spinner. Use inside an already-rendered layout
 *     when a guard is waiting on a secondary fetch (e.g.
 *     `PermissionGuard` after auth is ready but `user.permissions`
 *     is still coming back from `/api/permissions`).
 *
 * Keeping `role="status"` + `aria-live="polite"` + the spinner
 * classes in a single place means three previously-divergent copies
 * can no longer drift apart.
 */
export const AuthLoadingFallback: React.FC<AuthLoadingFallbackProps> = ({
  variant = 'full',
  labelKey,
}) => {
  const isFull = variant === 'full';
  const containerClass = isFull
    ? 'flex items-center justify-center min-h-screen'
    : 'flex items-center justify-center min-h-[40vh]';
  const spinnerClass = isFull
    ? 'animate-spin rounded-full h-8 w-8 border-b-2 border-primary'
    : 'animate-spin rounded-full h-6 w-6 border-b-2 border-primary';
  const textClass = isFull ? 'mt-2 text-muted-foreground' : 'mt-2 text-sm text-muted-foreground';
  const resolvedLabelKey = labelKey ?? (isFull ? 'ui.loading' : 'auth.loadingAuth');

  return (
    <div className={containerClass} role="status" aria-live="polite">
      <div className="flex flex-col items-center">
        <div className={spinnerClass} />
        <p className={textClass}>{i18n.t(resolvedLabelKey)}</p>
      </div>
    </div>
  );
};
